import { useCallback, useEffect, useRef } from 'react';
import {
  getAppCommitHash,
  getAppName,
  getAppVersion,
} from '@rsos/base-utils/metaTags';
import { POLL_INTERVAL_MS, subscribeToVersionMonitor } from './monitor';
import {
  fetchAppVersionInfo,
  isVersionStale,
  type AppVersionInfo,
} from './version';

export interface UseAppVersionMonitorOptions {
  enabled?: boolean;
  /**
   * Called when the deployed version differs from the loaded baseline.
   * May fire on every poll while the build stays stale. Handlers should
   * dedupe side effects, for example via toast.isActive.
   */
  onStaleVersion: (remote: AppVersionInfo) => void;
  pollIntervalMs?: number;
}

/**
 * Polls the host app for deployed version changes and invokes onStaleVersion when stale.
 */
export function useAppVersionMonitor({
  enabled = false,
  onStaleVersion,
  pollIntervalMs = POLL_INTERVAL_MS,
}: UseAppVersionMonitorOptions): void {
  const baselineRef = useRef<Pick<AppVersionInfo, 'version' | 'commitHash'>>({
    version: getAppVersion(),
    commitHash: getAppCommitHash(),
  });
  const appNameRef = useRef(getAppName());
  const onStaleVersionRef = useRef(onStaleVersion);

  useEffect(() => {
    onStaleVersionRef.current = onStaleVersion;
  }, [onStaleVersion]);

  const check = useCallback(async (): Promise<void> => {
    const appName = appNameRef.current;
    const baseline = baselineRef.current;

    if (!appName || !baseline.version) {
      return;
    }

    const remote = await fetchAppVersionInfo(appName);
    if (!remote?.version) {
      return;
    }

    if (isVersionStale(baseline, remote)) {
      onStaleVersionRef.current(remote);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return subscribeToVersionMonitor(check, pollIntervalMs);
  }, [check, enabled, pollIntervalMs]);
}
